import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ChatProvider } from './contexts/ChatContext';
import { LoadingProvider } from './contexts/LoadingContext';
import { SWRConfigProvider } from './contexts/SWRConfig';
import { ThemeProvider } from './contexts/ThemeContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { ConfirmProvider } from './components/ui';
import { SettingsProvider } from './contexts/SettingsContext';
import { TopbarProvider } from './contexts/TopbarContext';
import ErrorBoundary from './components/ErrorBoundary';
import GlobalLoading from './components/GlobalLoading';
import Login from './components/Login';
import MainLayout from './components/Layout/MainLayout';
import ProtectedRoute from './components/ProtectedRoute';
import OfficeVesselRedirect from './components/OfficeVesselRedirect';
import ManningAgentRouteGuard from './components/ManningAgentRouteGuard';
import { List as DashboardList } from './modules/dashboard';
import { List as NotificationsList } from './modules/notifications';
import { List as ApprovalsList, Detail as ApprovalDetail, Templates as ApprovalTemplates, Delegations as ApprovalDelegations } from './modules/approvals';
import {
  CatalogList as EFormsCatalog,
  BuilderList as EFormsBuilderList,
  FormStudioPage as EFormsFormStudioPage,
  FillPage as EFormsFillPage,
  SubmissionsList as EFormsSubmissionsList,
  SubmissionDetail as EFormsSubmissionDetail,
  ReportsPage as EFormsReportsPage,
  SavedReportViewPage as EFormsSavedReportViewPage,
  ReportBuilderList as EFormsReportBuilderList,
  EFormAppPagesList,
  EFormAppPageEditor,
  EFormAppPageView,
} from './modules/e-forms';
import { List as DocumentsList, New as DocumentsNew, Import as DocumentsImport, Detail as DocumentsDetail, BusinessRules as DocumentsBusinessRules } from './modules/documents';
import { List as UsersList, Form as UsersForm, Detail as UsersDetail, UserPermissionsPage, UserMenuPage } from './modules/admin/users';
import { ManningAgentsList, ManningAgentsForm, ManningAgentsDetail } from './modules/admin/manning-agents';
import AccountSettings from './modules/account-settings';
import AdminRoute from './components/AdminRoute';
import { DatabaseBrowserPage } from './modules/admin/database-browser';
import { ApplicationSettingsPage } from './modules/admin/settings';
import { ModuleManagementPage } from './modules/admin/module-management';
import { AiBillingPage } from './modules/admin/ai-billing';
import { ImpaCodeImportPage, AccountsPlanImportPage, CompaniesImportPage, JournalsImportPage } from './modules/admin/import-actions';
import { List as ItemUnitsList, Form as ItemUnitsForm, Detail as ItemUnitsDetail } from './modules/supplies/item-units';
import { List as ExcelDocumentTemplatesList } from './modules/supplies/excel-document-templates';
import { List as ReferenceTemplatesList } from './modules/system/reference-templates';
import { List as ItemsList, Form as ItemsForm, Detail as ItemsDetail, ItemHistory as ItemsItemHistory } from './modules/supplies/items';
import {
  List as StoreCatalogsList,
  Form as StoreCatalogsForm,
  Detail as StoreCatalogsDetail,
} from './modules/supplies/store-catalogs';
import {
  List as SupplyCasesList,
  Form as SupplyCasesForm,
  Detail as SupplyCasesDetail,
  CaseView as SupplyCasesCaseView,
} from './modules/supplies/supply-cases';
import { PortCallsPage } from './modules/port-calls';
import { List as CombinedForwardingsList } from './modules/forwardings';
import { PortCallsPage as SuppliesPortCallsPage } from './modules/supplies/port-calls';
import { PortCallsPage as SparesPortCallsPage } from './modules/spares/port-calls';
import {
  List as RequisitionsList,
  Form as RequisitionsForm,
  Detail as RequisitionsDetail,
} from './modules/supplies/requisitions';
import { LANDING_MODULE_ORDER, resolveLandingPathForModuleKey } from './config/permissionModules';
import {
  List as SupplierEnquiriesList,
  Form as SupplierEnquiriesForm,
  Detail as SupplierEnquiriesDetail,
} from './modules/supplies/supplier-enquiries';
import {
  List as SupplierQuotationsList,
  Form as SupplierQuotationsForm,
  Detail as SupplierQuotationsDetail,
} from './modules/supplies/supplier-quotations';
import {
  List as SupplyDeliveriesList,
  Form as SupplyDeliveriesForm,
  Detail as SupplyDeliveriesDetail,
} from './modules/supplies/supply-deliveries';
import {
  List as SupplyPurchaseOrdersList,
  Form as SupplyPurchaseOrdersForm,
  Detail as SupplyPurchaseOrdersDetail,
} from './modules/supplies/supply-purchase-orders';
import {
  List as SupplyForwardingStatusesList,
  Form as SupplyForwardingStatusesForm,
  Detail as SupplyForwardingStatusesDetail,
} from './modules/supplies/supply-forwarding-statuses';
import {
  List as SupplyForwardingsList,
  Form as SupplyForwardingsForm,
  Detail as SupplyForwardingsDetail,
} from './modules/supplies/supply-forwardings';
import {
  List as SupplyInvoicesList,
  Form as SupplyInvoicesForm,
  Detail as SupplyInvoicesDetail,
} from './modules/supplies/supply-invoices';
import {
  List as QuotationEvaluationsList,
  Form as QuotationEvaluationsForm,
  Detail as QuotationEvaluationsDetail,
} from './modules/supplies/quotation-evaluations';
import {
  List as SpareCasesList,
  Form as SpareCasesForm,
  Detail as SpareCasesDetail,
  CaseView as SpareCasesCaseView,
} from './modules/spares/spare-cases';
import {
  List as SpareRequisitionsList,
  Form as SpareRequisitionsForm,
  Detail as SpareRequisitionsDetail,
} from './modules/spares/requisitions';
import {
  List as SpareSupplierEnquiriesList,
  Form as SpareSupplierEnquiriesForm,
  Detail as SpareSupplierEnquiriesDetail,
} from './modules/spares/supplier-enquiries';
import {
  List as SpareSupplierQuotationsList,
  Form as SpareSupplierQuotationsForm,
  Detail as SpareSupplierQuotationsDetail,
} from './modules/spares/supplier-quotations';
import {
  List as SpareDeliveriesList,
  Form as SpareDeliveriesForm,
  Detail as SpareDeliveriesDetail,
} from './modules/spares/spare-deliveries';
import {
  List as SparePurchaseOrdersList,
  Form as SparePurchaseOrdersForm,
  Detail as SparePurchaseOrdersDetail,
} from './modules/spares/spare-purchase-orders';
import {
  List as SpareForwardingStatusesList,
  Form as SpareForwardingStatusesForm,
  Detail as SpareForwardingStatusesDetail,
} from './modules/spares/spare-forwarding-statuses';
import {
  List as PmsRecordStatusesList,
  Form as PmsRecordStatusesForm,
  Detail as PmsRecordStatusesDetail,
} from './modules/pms/pms-record-statuses';
import {
  List as PmsCriticalitiesList,
  Form as PmsCriticalitiesForm,
  Detail as PmsCriticalitiesDetail,
} from './modules/pms/pms-criticalities';
import {
  List as PmsEquipmentStatusesList,
  Form as PmsEquipmentStatusesForm,
  Detail as PmsEquipmentStatusesDetail,
} from './modules/pms/pms-equipment-statuses';
import {
  List as PmsMaintenanceStrategiesList,
  Form as PmsMaintenanceStrategiesForm,
  Detail as PmsMaintenanceStrategiesDetail,
} from './modules/pms/pms-maintenance-strategies';
import {
  List as PmsJobTypesList,
  Form as PmsJobTypesForm,
  Detail as PmsJobTypesDetail,
} from './modules/pms/pms-job-types';
import {
  List as PmsScheduleTypesList,
  Form as PmsScheduleTypesForm,
  Detail as PmsScheduleTypesDetail,
} from './modules/pms/pms-schedule-types';
import {
  List as PmsIntervalUnitsList,
  Form as PmsIntervalUnitsForm,
  Detail as PmsIntervalUnitsDetail,
} from './modules/pms/pms-interval-units';
import {
  List as PmsWorkOrderStatusesList,
  Form as PmsWorkOrderStatusesForm,
  Detail as PmsWorkOrderStatusesDetail,
} from './modules/pms/pms-work-order-statuses';
import {
  List as PmsWorkOrderSourcesList,
  Form as PmsWorkOrderSourcesForm,
  Detail as PmsWorkOrderSourcesDetail,
} from './modules/pms/pms-work-order-sources';
import {
  List as PmsApprovalStatusesList,
  Form as PmsApprovalStatusesForm,
  Detail as PmsApprovalStatusesDetail,
} from './modules/pms/pms-approval-statuses';
import {
  List as PmsStepStatusesList,
  Form as PmsStepStatusesForm,
  Detail as PmsStepStatusesDetail,
} from './modules/pms/pms-step-statuses';
import {
  List as PmsCounterTypesList,
  Form as PmsCounterTypesForm,
  Detail as PmsCounterTypesDetail,
} from './modules/pms/pms-counter-types';
import {
  List as PmsCounterSourcesList,
  Form as PmsCounterSourcesForm,
  Detail as PmsCounterSourcesDetail,
} from './modules/pms/pms-counter-sources';
import {
  List as PmsMeasurementResultsList,
  Form as PmsMeasurementResultsForm,
  Detail as PmsMeasurementResultsDetail,
} from './modules/pms/pms-measurement-results';
import {
  List as PmsDefectSeveritiesList,
  Form as PmsDefectSeveritiesForm,
  Detail as PmsDefectSeveritiesDetail,
} from './modules/pms/pms-defect-severities';
import {
  List as PmsDefectStatusesList,
  Form as PmsDefectStatusesForm,
  Detail as PmsDefectStatusesDetail,
} from './modules/pms/pms-defect-statuses';
import {
  List as PmsEquipmentInstanceStatusesList,
  Form as PmsEquipmentInstanceStatusesForm,
  Detail as PmsEquipmentInstanceStatusesDetail,
} from './modules/pms/pms-equipment-instance-statuses';
import {
  List as PmsSafetyRequirementTypesList,
  Form as PmsSafetyRequirementTypesForm,
  Detail as PmsSafetyRequirementTypesDetail,
} from './modules/pms/pms-safety-requirement-types';
import {
  List as PmsDocumentTypesList,
  Form as PmsDocumentTypesForm,
  Detail as PmsDocumentTypesDetail,
} from './modules/pms/pms-document-types';
import {
  List as PmsVerificationStatusesList,
  Form as PmsVerificationStatusesForm,
  Detail as PmsVerificationStatusesDetail,
} from './modules/pms/pms-verification-statuses';
import {
  List as PmsDepartmentTypesList,
  Form as PmsDepartmentTypesForm,
  Detail as PmsDepartmentTypesDetail,
} from './modules/pms/pms-department-types';
import { PmsHierarchyPage } from './modules/pms/hierarchy';
import { PmsDueJobsPage } from './modules/pms/due-jobs';
import {
  List as PmsDepartmentsList,
  Form as PmsDepartmentsForm,
  Detail as PmsDepartmentsDetail,
} from './modules/pms/pms-departments';
import {
  List as PmsEquipmentSystemsList,
  Form as PmsEquipmentSystemsForm,
  Detail as PmsEquipmentSystemsDetail,
} from './modules/pms/pms-equipment-systems';
import {
  List as PmsComponentsList,
  Form as PmsComponentsForm,
  Detail as PmsComponentsDetail,
} from './modules/pms/pms-components';
import {
  List as PmsSubComponentsList,
  Form as PmsSubComponentsForm,
  Detail as PmsSubComponentsDetail,
} from './modules/pms/pms-sub-components';
import {
  List as PmsEquipmentList,
  Form as PmsEquipmentForm,
  Detail as PmsEquipmentDetail,
} from './modules/pms/pms-equipment';
import {
  List as PmsEquipmentInstancesList,
  Form as PmsEquipmentInstancesForm,
  Detail as PmsEquipmentInstancesDetail,
} from './modules/pms/pms-equipment-instances';
import {
  List as PmsMaintenanceJobsList,
  Form as PmsMaintenanceJobsForm,
  Detail as PmsMaintenanceJobsDetail,
} from './modules/pms/pms-maintenance-jobs';
import {
  List as PmsMaintenanceCountersList,
  Form as PmsMaintenanceCountersForm,
  Detail as PmsMaintenanceCountersDetail,
} from './modules/pms/pms-maintenance-counters';
import {
  List as PmsWorkOrdersList,
  Form as PmsWorkOrdersForm,
  Detail as PmsWorkOrdersDetail,
} from './modules/pms/pms-work-orders';
import {
  List as PmsDefectsList,
  Form as PmsDefectsForm,
  Detail as PmsDefectsDetail,
} from './modules/pms/pms-defects';
import {
  List as NoonReportsList,
  Form as NoonReportsForm,
  Detail as NoonReportsDetail,
} from './modules/operations/noon-reports';
import {
  List as PmsToolsList,
  Form as PmsToolsForm,
  Detail as PmsToolsDetail,
} from './modules/pms/pms-tools';
import {
  List as PmsDocumentsList,
  Form as PmsDocumentsForm,
  Detail as PmsDocumentsDetail,
} from './modules/pms/pms-documents';
import {
  List as SpareForwardingsList,
  Form as SpareForwardingsForm,
  Detail as SpareForwardingsDetail,
} from './modules/spares/spare-forwardings';
import {
  List as SpareInvoicesList,
  Form as SpareInvoicesForm,
  Detail as SpareInvoicesDetail,
} from './modules/spares/spare-invoices';
import {
  List as SpareQuotationEvaluationsList,
  Form as SpareQuotationEvaluationsForm,
  Detail as SpareQuotationEvaluationsDetail,
} from './modules/spares/quotation-evaluations';
import {
  List as SpareLibrariesList,
  Detail as SpareLibrariesDetail,
  SpareLibraryEditRedirect,
} from './modules/spares/spare-libraries';
import {
  List as SpareItemsList,
  Form as SpareItemsForm,
  Detail as SpareItemsDetail,
} from './modules/spares/spare-items';
import { List as CountriesList, Form as CountriesForm, Detail as CountriesDetail } from './modules/parameters/countries';
import { List as CurrenciesList, Form as CurrenciesForm, Detail as CurrenciesDetail } from './modules/parameters/currencies';
import { List as DepartmentsList, Form as DepartmentsForm, Detail as DepartmentsDetail } from './modules/parameters/departments';
import {
  Form as ManagementCompaniesForm,
  Detail as ManagementCompaniesDetail,
} from './modules/parameters/management-companies';
import {
  List as CrewLookupsList,
  Form as CrewLookupsForm,
  Detail as CrewLookupsDetail,
} from './modules/parameters/crew-lookups';
import { CrewReferenceWorkspace } from './modules/parameters/crew-reference';
import {
  GeneralReferenceWorkspace,
  PurchasingReferenceWorkspace,
  SuppliesCatalogRedirect,
  SparesCatalogRedirect,
  PmsReferenceWorkspace,
  FinanceReferenceWorkspace,
} from './modules/parameters/reference-workspaces';
import {
  Form as CrewWageColumnsForm,
  Detail as CrewWageColumnsDetail,
} from './modules/parameters/crew-wage-columns';
import {
  Form as CrewWageScalesForm,
  Detail as CrewWageScalesDetail,
} from './modules/parameters/crew-wage-scales';
import {
  Form as CrewHolidaysForm,
  Detail as CrewHolidaysDetail,
} from './modules/parameters/crew-holidays';
import {
  List as CrewQuestionnaireKindsList,
  Form as CrewQuestionnaireKindsForm,
  Detail as CrewQuestionnaireKindsDetail,
} from './modules/parameters/crew-questionnaire-kinds';
import {
  Form as CrewQuestionnairesForm,
  Detail as CrewQuestionnairesDetail,
} from './modules/parameters/crew-questionnaires';
import {
  List as StoreCategoriesList,
  Form as StoreCategoriesForm,
  Detail as StoreCategoriesDetail,
} from './modules/parameters/store-categories';
import { List as VesselsList, Form as VesselsForm, Detail as VesselsDetail } from './modules/parameters/vessels';
import { List as PortsList, Form as PortsForm, Detail as PortsDetail } from './modules/parameters/ports';
import { List as EntitiesList, Form as EntitiesForm, Detail as EntitiesDetail } from './modules/entities';
import { List as CrewPoolList, Form as CrewForm, Detail as CrewDetail, FileView as CrewFileView, CrewFileReportPage, CrewListPrintPage, CrewListPage, OfficerMatrixPage, RightShipMatrixPage, CrewChangesPerVesselPage, CertExpiryPage, CertGapsPage, ReliefBoardPage, CoeWatchlistPage, AppraisalNrPage, NationalityManningPage, WageAllotteePage, PrevServiceExperiencePage, OnBoardStatusPage, OnboardStatusReportPage, ReplacementPlannerPage, ReplacementPlanReportPage, TravelTicketsPage, TravelTicketDetailPage, TravelTicketFormPage, CrewReportHub, CrewStatisticsHub, OperationsStatisticsPage, ComplianceStatisticsPage, PeoplePayrollStatisticsPage, MixChartsStatisticsPage, StatisticsBreakdownPage, OnboardStatisticsPage, OnboardCountReportPage, AvailableStatisticsPage, AvailableCountReportPage, NotRecommendedStatisticsPage, NrCountReportPage, ComplianceCountStatisticsPage, ComplianceCountReportPage } from './modules/crew';
import CrewChangesCostReportPage from './modules/crew/reports/CrewChangesCostReportPage';
import ReliefBoardReportPage from './modules/crew/reports/ReliefBoardReportPage';
import CoeWatchlistReportPage from './modules/crew/reports/CoeWatchlistReportPage';
import CertGapsReportPage from './modules/crew/reports/CertGapsReportPage';
import RankRequirementsReportPage from './modules/crew/reports/RankRequirementsReportPage';
import RankRequirementsByNationalityReportPage from './modules/crew/reports/RankRequirementsByNationalityReportPage';
import StatsOnboardReportPage from './modules/crew/reports/StatsOnboardReportPage';
import StatsAvailableReportPage from './modules/crew/reports/StatsAvailableReportPage';
import StatsNotRecommendedReportPage from './modules/crew/reports/StatsNotRecommendedReportPage';
import StatsComplianceReportPage from './modules/crew/reports/StatsComplianceReportPage';
import { List as MgaList, Form as MgaForm, Dashboard as MgaDashboard, PeriodGrid as MgaPeriodGrid, Payslip as MgaPayslip, ReportPage as MgaReportPage, PostingConfig as MgaPostingConfig, SheetColumnsList as MgaSheetColumnsList, SheetColumnsForm as MgaSheetColumnsForm, SheetColumnsDetail as MgaSheetColumnsDetail } from './modules/mga';
import {
  List as WrhList,
  CompliancePage as WrhCompliancePage,
  WeeklyReportPage as WrhWeeklyReportPage,
  RulesPage as WrhRulesPage,
  ArrangementsPage as WrhArrangementsPage,
  ArrangementsReportPage as WrhArrangementsReportPage,
  RestRecordReportPage as WrhRestRecordReportPage,
  MonthSheetPage as WrhMonthSheetPage,
  RulesReportPage as WrhRulesReportPage,
  ComplianceReportPage as WrhComplianceReportPage,
  WeeklySummaryReportPage as WrhWeeklySummaryReportPage,
} from './modules/wrh';
import { List as OperationTypesList, Form as OperationTypesForm, Detail as OperationTypesDetail } from './modules/vessel-operation-types';
import { VesselOperationsSchedulePage, VesselOperationsReportPage } from './modules/vessel-operations';
import { List as AccDocumentTypesList, Form as AccDocumentTypesForm, Detail as AccDocumentTypesDetail } from './modules/accounting/document-types';
import { List as AccAccountTypesList, Form as AccAccountTypesForm, Detail as AccAccountTypesDetail } from './modules/accounting/account-types';
import { List as AccOperationalTypesList, Form as AccOperationalTypesForm, Detail as AccOperationalTypesDetail } from './modules/accounting/operational-types';
import { List as AccCompaniesList, Form as AccCompaniesForm, Detail as AccCompaniesDetail } from './modules/accounting/companies';
import { List as AccAccountsList, Form as AccAccountsForm, Detail as AccAccountsDetail } from './modules/accounting/accounts';
import { List as AccPeriodsList, Form as AccPeriodsForm, Detail as AccPeriodsDetail } from './modules/accounting/periods';
import { List as AccCurrencyRatesList, Form as AccCurrencyRatesForm, Detail as AccCurrencyRatesDetail } from './modules/accounting/currency-rates';
import { StatementLines as AccStatementLines } from './modules/accounting/statement-lines';
import { List as AccDocumentsList, Form as AccDocumentsForm, Detail as AccDocumentsDetail } from './modules/accounting/documents';
import { PaymentForm } from './modules/accounting/payments';
import { InvoiceForm } from './modules/accounting/invoices';
import { TrialBalance, Aging, AnalyticalLedgers, LedgerKartela, JournalReport, OpenItemsDetail, LinkStatement, JournalRegister, TrialBalanceByCompany, GeneralLedger, ProfitAndLoss, BalanceSheet, IntercompanyReport, RunningCostByVessel, DepartmentAnalysis, PeriodCloseChecklist, AuditTrail, ReportHub } from './modules/accounting/reports';
import PublicRfqPage from './modules/public-rfq/PublicRfqPage';

const EFormPlatformRedirect: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/e-forms/builder/${id}?section=access`} replace />;
};

const SuppliersToEntitiesRedirect: React.FC<{ suffix?: string }> = ({ suffix = '' }) => {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/entities/${id}${suffix}`} replace />;
};

const CrewTravelsToChangesCostRedirect: React.FC<{
  suffix?: string;
  list?: boolean;
  toNew?: boolean;
}> = ({ suffix = '', list = false, toNew = false }) => {
  const { id } = useParams<{ id: string }>();
  const { search } = useLocation();
  if (list) {
    return <Navigate to={`/crew/crew-changes-cost${search}`} replace />;
  }
  if (toNew) {
    return <Navigate to={`/crew/crew-changes-cost/new${search}`} replace />;
  }
  return <Navigate to={`/crew/crew-changes-cost/${id}${suffix}${search}`} replace />;
};

const CrewImoPrintRedirect: React.FC = () => {
  const { search } = useLocation();
  return <Navigate to={`/crew/list/print/imo${search}`} replace />;
};

const CrewOpsReportsRedirect: React.FC<{ print?: boolean }> = ({ print = false }) => {
  const { focus } = useParams<{ focus?: string }>();
  const { search } = useLocation();
  const rest = focus ? `/${focus}` : '';
  const suffix = print ? '/print' : '';
  return <Navigate to={`/crew/reports/operations${rest}${suffix}${search}`} replace />;
};

const AppRoutes: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();
  const isPublicRfq = location.pathname.startsWith('/rfq/');

  if (isLoading && !isPublicRfq) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={isAuthenticated ? <Navigate to="/" replace /> : <Login />} />
      <Route path="/rfq/:token" element={<PublicRfqPage />} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <DefaultLandingRoute />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <MainLayout>
              <DashboardList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/notifications"
        element={
          <ProtectedRoute>
            <MainLayout>
              <NotificationsList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route path="/approvals" element={<ProtectedRoute><MainLayout><ApprovalsList /></MainLayout></ProtectedRoute>} />
      <Route path="/approvals/templates" element={<ProtectedRoute><MainLayout><ApprovalTemplates /></MainLayout></ProtectedRoute>} />
      <Route path="/approvals/delegations" element={<ProtectedRoute><MainLayout><ApprovalDelegations /></MainLayout></ProtectedRoute>} />
      <Route path="/approvals/:id" element={<ProtectedRoute><MainLayout><ApprovalDetail /></MainLayout></ProtectedRoute>} />
      <Route path="/e-forms" element={<ProtectedRoute><MainLayout><EFormsCatalog /></MainLayout></ProtectedRoute>} />
      <Route path="/e-forms/submissions" element={<ProtectedRoute><MainLayout><EFormsSubmissionsList /></MainLayout></ProtectedRoute>} />
      <Route path="/e-forms/submissions/:id/edit" element={<ProtectedRoute><MainLayout><EFormsFillPage /></MainLayout></ProtectedRoute>} />
      <Route path="/e-forms/submissions/:id" element={<ProtectedRoute><MainLayout><EFormsSubmissionDetail /></MainLayout></ProtectedRoute>} />
      <Route path="/e-forms/builder" element={<ProtectedRoute><MainLayout><EFormsBuilderList /></MainLayout></ProtectedRoute>} />
      <Route path="/e-forms/builder/:id/platform" element={<ProtectedRoute><MainLayout><EFormPlatformRedirect /></MainLayout></ProtectedRoute>} />
      <Route path="/e-forms/builder/:id" element={<ProtectedRoute><MainLayout><EFormsFormStudioPage /></MainLayout></ProtectedRoute>} />
      <Route path="/e-forms/report-builder" element={<ProtectedRoute><MainLayout><EFormsReportBuilderList /></MainLayout></ProtectedRoute>} />
      <Route path="/e-forms/apps" element={<ProtectedRoute><MainLayout><EFormAppPagesList /></MainLayout></ProtectedRoute>} />
      <Route path="/e-forms/apps/new" element={<ProtectedRoute><MainLayout><EFormAppPageEditor /></MainLayout></ProtectedRoute>} />
      <Route path="/e-forms/apps/:pageId/edit" element={<ProtectedRoute><MainLayout><EFormAppPageEditor /></MainLayout></ProtectedRoute>} />
      <Route path="/e-forms/apps/:pageId" element={<ProtectedRoute><MainLayout><EFormAppPageView /></MainLayout></ProtectedRoute>} />
      <Route path="/e-forms/:definitionId/fill" element={<ProtectedRoute><MainLayout><EFormsFillPage /></MainLayout></ProtectedRoute>} />
      <Route path="/e-forms/:definitionId/submissions" element={<ProtectedRoute><MainLayout><EFormsSubmissionsList /></MainLayout></ProtectedRoute>} />
      <Route path="/e-forms/:definitionId/reports/:reportId" element={<ProtectedRoute><MainLayout><EFormsSavedReportViewPage /></MainLayout></ProtectedRoute>} />
      <Route path="/e-forms/:definitionId/reports" element={<ProtectedRoute><MainLayout><EFormsReportsPage /></MainLayout></ProtectedRoute>} />
      <Route path="/documents" element={<ProtectedRoute><MainLayout><DocumentsList /></MainLayout></ProtectedRoute>} />
      <Route path="/documents/new" element={<ProtectedRoute><MainLayout><DocumentsNew /></MainLayout></ProtectedRoute>} />
      <Route path="/documents/import" element={<ProtectedRoute><MainLayout><DocumentsImport /></MainLayout></ProtectedRoute>} />
      <Route path="/documents/business-rules" element={<ProtectedRoute><MainLayout><DocumentsBusinessRules /></MainLayout></ProtectedRoute>} />
      <Route path="/documents/:id" element={<ProtectedRoute><MainLayout><DocumentsDetail /></MainLayout></ProtectedRoute>} />
      <Route
        path="/admin/users"
        element={
          <ProtectedRoute>
            <MainLayout>
              <UsersList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/users/new"
        element={
          <ProtectedRoute>
            <MainLayout>
              <UsersForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/users/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <UsersDetail />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/users/:id/edit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <UsersForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/users/:id/permissions"
        element={
          <ProtectedRoute>
            <MainLayout>
              <UserPermissionsPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/users/:id/menu"
        element={
          <ProtectedRoute>
            <MainLayout>
              <UserMenuPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/manning-agents"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ManningAgentsList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/manning-agents/new"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ManningAgentsForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/manning-agents/:id/edit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ManningAgentsForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/manning-agents/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ManningAgentsDetail />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route path="/parameters/countries" element={<Navigate to="/parameters/general?section=countries" replace />} />
      <Route
        path="/parameters/countries/new"
        element={
          <ProtectedRoute>
            <MainLayout>
              <CountriesForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/parameters/countries/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <CountriesDetail />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/parameters/countries/:id/edit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <CountriesForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route path="/parameters/currencies" element={<Navigate to="/parameters/general?section=currencies" replace />} />
      <Route
        path="/parameters/currencies/new"
        element={
          <ProtectedRoute>
            <MainLayout>
              <CurrenciesForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/parameters/currencies/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <CurrenciesDetail />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/parameters/currencies/:id/edit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <CurrenciesForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route path="/parameters/departments" element={<Navigate to="/parameters/general?section=departments" replace />} />
      <Route
        path="/parameters/departments/new"
        element={
          <ProtectedRoute>
            <MainLayout>
              <DepartmentsForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/parameters/departments/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <DepartmentsDetail />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/parameters/departments/:id/edit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <DepartmentsForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/parameters/management-companies"
        element={<Navigate to="/parameters/general?section=management-companies" replace />}
      />
      <Route
        path="/parameters/management-companies/new"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ManagementCompaniesForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/parameters/management-companies/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ManagementCompaniesDetail />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/parameters/management-companies/:id/edit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ManagementCompaniesForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/parameters/crew"
        element={
          <ProtectedRoute>
            <MainLayout>
              <CrewReferenceWorkspace />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/parameters/general"
        element={
          <ProtectedRoute>
            <MainLayout>
              <GeneralReferenceWorkspace />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/parameters/purchasing"
        element={
          <ProtectedRoute>
            <MainLayout>
              <PurchasingReferenceWorkspace />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route path="/parameters/supplies-catalog" element={<SuppliesCatalogRedirect />} />
      <Route path="/parameters/spares-catalog" element={<SparesCatalogRedirect />} />
      <Route
        path="/parameters/pms"
        element={
          <ProtectedRoute>
            <MainLayout>
              <PmsReferenceWorkspace />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/parameters/finance"
        element={
          <ProtectedRoute>
            <MainLayout>
              <FinanceReferenceWorkspace />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/parameters/crew/wage-columns"
        element={<Navigate to="/parameters/crew?section=wage-columns" replace />}
      />
      <Route
        path="/parameters/crew/wage-columns/new"
        element={
          <ProtectedRoute>
            <MainLayout>
              <CrewWageColumnsForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/parameters/crew/wage-columns/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <CrewWageColumnsDetail />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/parameters/crew/wage-columns/:id/edit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <CrewWageColumnsForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/parameters/crew/wage-scales"
        element={<Navigate to="/parameters/crew?section=wage-scales" replace />}
      />
      <Route
        path="/parameters/crew/wage-scales/new"
        element={
          <ProtectedRoute>
            <MainLayout>
              <CrewWageScalesForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/parameters/crew/wage-scales/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <CrewWageScalesDetail />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/parameters/crew/wage-scales/:id/edit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <CrewWageScalesForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/parameters/crew/holidays"
        element={<Navigate to="/parameters/crew?section=holidays" replace />}
      />
      <Route
        path="/parameters/crew/holidays/new"
        element={
          <ProtectedRoute>
            <MainLayout>
              <CrewHolidaysForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/parameters/crew/holidays/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <CrewHolidaysDetail />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/parameters/crew/holidays/:id/edit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <CrewHolidaysForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route path="/parameters/crew/questionnaire-kinds" element={<ProtectedRoute><MainLayout><CrewQuestionnaireKindsList /></MainLayout></ProtectedRoute>} />
      <Route path="/parameters/crew/questionnaire-kinds/new" element={<ProtectedRoute><MainLayout><CrewQuestionnaireKindsForm /></MainLayout></ProtectedRoute>} />
      <Route path="/parameters/crew/questionnaire-kinds/:id" element={<ProtectedRoute><MainLayout><CrewQuestionnaireKindsDetail /></MainLayout></ProtectedRoute>} />
      <Route path="/parameters/crew/questionnaire-kinds/:id/edit" element={<ProtectedRoute><MainLayout><CrewQuestionnaireKindsForm /></MainLayout></ProtectedRoute>} />
      <Route path="/parameters/crew/questionnaires" element={<Navigate to="/parameters/crew?section=questionnaires" replace />} />
      <Route path="/parameters/crew/questionnaires/new" element={<ProtectedRoute><MainLayout><CrewQuestionnairesForm /></MainLayout></ProtectedRoute>} />
      <Route path="/parameters/crew/questionnaires/:id" element={<ProtectedRoute><MainLayout><CrewQuestionnairesDetail /></MainLayout></ProtectedRoute>} />
      <Route path="/parameters/crew/questionnaires/:id/edit" element={<ProtectedRoute><MainLayout><CrewQuestionnairesForm /></MainLayout></ProtectedRoute>} />
      <Route
        path="/parameters/crew/:kind"
        element={
          <ProtectedRoute>
            <MainLayout>
              <CrewLookupsList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/parameters/crew/:kind/new"
        element={
          <ProtectedRoute>
            <MainLayout>
              <CrewLookupsForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/parameters/crew/:kind/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <CrewLookupsDetail />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/parameters/crew/:kind/:id/edit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <CrewLookupsForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route path="/parameters/store-categories" element={<Navigate to="/parameters/purchasing?section=supplies-categories" replace />} />
      <Route
        path="/parameters/store-categories/new"
        element={
          <ProtectedRoute>
            <MainLayout>
              <StoreCategoriesForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/parameters/store-categories/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <StoreCategoriesDetail />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/parameters/store-categories/:id/edit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <StoreCategoriesForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route path="/parameters/ports" element={<Navigate to="/parameters/general?section=ports" replace />} />
      <Route
        path="/parameters/ports/new"
        element={
          <ProtectedRoute>
            <MainLayout>
              <PortsForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/parameters/ports/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <PortsDetail />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/parameters/ports/:id/edit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <PortsForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route path="/parameters/vessels" element={<Navigate to="/parameters/general?section=vessels" replace />} />
      <Route
        path="/parameters/vessels/new"
        element={
          <ProtectedRoute>
            <MainLayout>
              <VesselsForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/parameters/vessels/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <VesselsDetail />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/parameters/vessels/:id/edit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <VesselsForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route path="/entities" element={<Navigate to="/parameters/general?section=entities" replace />} />
      <Route
        path="/entities/new"
        element={
          <ProtectedRoute>
            <MainLayout>
              <EntitiesForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/entities/:id/edit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <EntitiesForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/entities/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <EntitiesDetail />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route path="/suppliers" element={<Navigate to="/entities" replace />} />
      <Route path="/suppliers/new" element={<Navigate to="/entities/new" replace />} />
      <Route path="/suppliers/:id/edit" element={<SuppliersToEntitiesRedirect suffix="/edit" />} />
      <Route path="/suppliers/:id" element={<SuppliersToEntitiesRedirect />} />
      <Route
        path="/crew"
        element={
          <ProtectedRoute>
            <MainLayout>
              <CrewPoolList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/mga"
        element={
          <ProtectedRoute>
            <MainLayout>
              <MgaList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/mga/new"
        element={
          <ProtectedRoute>
            <MainLayout>
              <MgaForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/mga/settings/posting"
        element={
          <ProtectedRoute>
            <MainLayout>
              <MgaPostingConfig />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route path="/mga/settings/sheet-columns/*" element={<Navigate to="/system/mga-sheet-columns" replace />} />
      <Route path="/mga/settings/sheet-columns" element={<Navigate to="/system/mga-sheet-columns" replace />} />
      <Route
        path="/system/mga-sheet-columns"
        element={
          <ProtectedRoute>
            <MainLayout>
              <MgaSheetColumnsList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/system/mga-sheet-columns/new"
        element={
          <ProtectedRoute>
            <MainLayout>
              <MgaSheetColumnsForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/system/mga-sheet-columns/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <MgaSheetColumnsDetail />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/system/mga-sheet-columns/:id/edit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <MgaSheetColumnsForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/mga/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <MgaDashboard />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/mga/:id/sheet"
        element={
          <ProtectedRoute>
            <MainLayout>
              <MgaPeriodGrid />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/mga/:id/lines/:lineId"
        element={
          <ProtectedRoute>
            <MainLayout>
              <MgaPayslip />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/mga/:id/reports/:reportKey"
        element={
          <ProtectedRoute>
            <MainLayout>
              <MgaReportPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/wrh"
        element={
          <ProtectedRoute>
            <MainLayout>
              <WrhList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/wrh/new"
        element={<Navigate to="/wrh/sheet" replace />}
      />
      <Route
        path="/wrh/compliance"
        element={
          <ProtectedRoute>
            <MainLayout>
              <WrhCompliancePage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/wrh/compliance/report"
        element={
          <ProtectedRoute>
            <MainLayout>
              <WrhComplianceReportPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/wrh/reports"
        element={
          <ProtectedRoute>
            <MainLayout>
              <WrhWeeklyReportPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/wrh/reports/report"
        element={
          <ProtectedRoute>
            <MainLayout>
              <WrhWeeklySummaryReportPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/wrh/rules"
        element={
          <ProtectedRoute>
            <MainLayout>
              <WrhRulesPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/wrh/rules/report"
        element={
          <ProtectedRoute>
            <MainLayout>
              <WrhRulesReportPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/wrh/arrangements/report"
        element={
          <ProtectedRoute>
            <MainLayout>
              <WrhArrangementsReportPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/wrh/arrangements"
        element={
          <ProtectedRoute>
            <MainLayout>
              <WrhArrangementsPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/wrh/sheet/rest-record"
        element={
          <ProtectedRoute>
            <MainLayout>
              <WrhRestRecordReportPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/wrh/sheet"
        element={
          <ProtectedRoute>
            <MainLayout>
              <WrhMonthSheetPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/wrh/operation-types"
        element={
          <ProtectedRoute>
            <MainLayout>
              <OperationTypesList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/wrh/operation-types/new"
        element={
          <ProtectedRoute>
            <MainLayout>
              <OperationTypesForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/wrh/operation-types/:id/edit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <OperationTypesForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/wrh/operation-types/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <OperationTypesDetail />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/vessel-operations"
        element={
          <ProtectedRoute>
            <MainLayout>
              <VesselOperationsSchedulePage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/vessel-operations/report"
        element={
          <ProtectedRoute>
            <MainLayout>
              <VesselOperationsReportPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route path="/wrh/:id" element={<Navigate to="/wrh/sheet" replace />} />
      <Route
        path="/crew/list"
        element={
          <ProtectedRoute>
            <MainLayout>
              <CrewListPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/crew/officer-matrix"
        element={
          <ProtectedRoute>
            <MainLayout>
              <OfficerMatrixPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/crew/rightship-matrix"
        element={
          <ProtectedRoute>
            <MainLayout>
              <RightShipMatrixPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/crew/changes-per-vessel"
        element={
          <ProtectedRoute>
            <MainLayout>
              <CrewChangesPerVesselPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/crew/certs-expiring"
        element={
          <ProtectedRoute>
            <MainLayout>
              <CertExpiryPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/crew/certificate-requirements"
        element={<Navigate to="/parameters/crew?section=certificate-requirements" replace />}
      />
      <Route
        path="/crew/certificate-requirements/print/by-nationality"
        element={
          <ProtectedRoute>
            <MainLayout>
              <RankRequirementsByNationalityReportPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/crew/certificate-requirements/print"
        element={
          <ProtectedRoute>
            <MainLayout>
              <RankRequirementsReportPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/crew/rank-requirements/print/by-nationality"
        element={<Navigate to={`/crew/certificate-requirements/print/by-nationality${window.location.search}`} replace />}
      />
      <Route
        path="/crew/rank-requirements/print"
        element={<Navigate to={`/crew/certificate-requirements/print${window.location.search}`} replace />}
      />
      <Route
        path="/crew/rank-requirements"
        element={<Navigate to={`/crew/certificate-requirements${window.location.search}`} replace />}
      />
      <Route
        path="/crew/reports"
        element={
          <ProtectedRoute>
            <MainLayout>
              <CrewReportHub />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/crew/statistics"
        element={
          <ProtectedRoute>
            <MainLayout>
              <CrewStatisticsHub />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/crew/statistics/onboard"
        element={
          <ProtectedRoute>
            <MainLayout>
              <OnboardStatisticsPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/crew/statistics/onboard/print"
        element={
          <ProtectedRoute>
            <MainLayout>
              <OnboardCountReportPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/crew/statistics/available"
        element={
          <ProtectedRoute>
            <MainLayout>
              <AvailableStatisticsPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/crew/statistics/available/print"
        element={
          <ProtectedRoute>
            <MainLayout>
              <AvailableCountReportPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/crew/statistics/not-recommended"
        element={
          <ProtectedRoute>
            <MainLayout>
              <NotRecommendedStatisticsPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/crew/statistics/not-recommended/print"
        element={
          <ProtectedRoute>
            <MainLayout>
              <NrCountReportPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/crew/statistics/nationalities"
        element={
          <ProtectedRoute>
            <MainLayout>
              <NationalityManningPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/crew/reports/operations/onboard/print"
        element={
          <ProtectedRoute>
            <MainLayout>
              <StatsOnboardReportPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/crew/reports/operations/available/print"
        element={
          <ProtectedRoute>
            <MainLayout>
              <StatsAvailableReportPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/crew/reports/operations/not-recommended/print"
        element={
          <ProtectedRoute>
            <MainLayout>
              <StatsNotRecommendedReportPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/crew/reports/operations"
        element={
          <ProtectedRoute>
            <MainLayout>
              <OperationsStatisticsPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/crew/reports/operations/:focus"
        element={
          <ProtectedRoute>
            <MainLayout>
              <OperationsStatisticsPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/crew/reports/compliance/cert-gaps/print"
        element={<Navigate to="/crew/reports" replace />}
      />
      <Route
        path="/crew/reports/compliance/cert-gaps"
        element={<Navigate to="/crew/reports" replace />}
      />
      <Route
        path="/crew/reports/compliance/:focus/print"
        element={
          <ProtectedRoute>
            <MainLayout>
              <StatsComplianceReportPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/crew/reports/compliance"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ComplianceStatisticsPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/crew/reports/compliance/:focus"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ComplianceStatisticsPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/crew/statistics/operations/:focus/print"
        element={<CrewOpsReportsRedirect print />}
      />
      <Route path="/crew/statistics/operations/:focus" element={<CrewOpsReportsRedirect />} />
      <Route path="/crew/statistics/operations" element={<CrewOpsReportsRedirect />} />
      <Route
        path="/crew/statistics/compliance/cert-gaps/print"
        element={<Navigate to="/crew/statistics" replace />}
      />
      <Route
        path="/crew/statistics/compliance/cert-gaps"
        element={<Navigate to="/crew/statistics" replace />}
      />
      <Route
        path="/crew/statistics/compliance/intention-overdue/print"
        element={<Navigate to="/crew/statistics" replace />}
      />
      <Route
        path="/crew/statistics/compliance/intention-overdue"
        element={<Navigate to="/crew/statistics" replace />}
      />
      <Route
        path="/crew/statistics/compliance/nationalities/print"
        element={<Navigate to="/crew/statistics" replace />}
      />
      <Route
        path="/crew/statistics/compliance/nationalities"
        element={<Navigate to="/crew/statistics" replace />}
      />
      <Route
        path="/crew/statistics/compliance/coe-soon/print"
        element={<Navigate to="/crew/statistics" replace />}
      />
      <Route
        path="/crew/statistics/compliance/coe-soon"
        element={<Navigate to="/crew/statistics" replace />}
      />
      <Route
        path="/crew/statistics/compliance/:focus/print"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ComplianceCountReportPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/crew/statistics/compliance"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ComplianceCountStatisticsPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/crew/statistics/compliance/:focus"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ComplianceCountStatisticsPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/crew/statistics/people-payroll"
        element={
          <ProtectedRoute>
            <MainLayout>
              <PeoplePayrollStatisticsPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/crew/statistics/people-payroll/:focus"
        element={
          <ProtectedRoute>
            <MainLayout>
              <PeoplePayrollStatisticsPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/crew/statistics/mix"
        element={
          <ProtectedRoute>
            <MainLayout>
              <MixChartsStatisticsPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/crew/statistics/mix/:focus"
        element={
          <ProtectedRoute>
            <MainLayout>
              <MixChartsStatisticsPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/crew/statistics/:kind/:groupBy"
        element={
          <ProtectedRoute>
            <MainLayout>
              <StatisticsBreakdownPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/crew/cert-gaps"
        element={
          <ProtectedRoute>
            <MainLayout>
              <CertGapsPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/crew/cert-gaps/print"
        element={
          <ProtectedRoute>
            <MainLayout>
              <CertGapsReportPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/crew/relief-board"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ReliefBoardPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/crew/relief-board/print"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ReliefBoardReportPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/crew/coe-watchlist"
        element={
          <ProtectedRoute>
            <MainLayout>
              <CoeWatchlistPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/crew/coe-watchlist/print"
        element={
          <ProtectedRoute>
            <MainLayout>
              <CoeWatchlistReportPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route path="/crew/medicals-expiring" element={<Navigate to="/crew/reports" replace />} />
      <Route
        path="/crew/appraisal-nr"
        element={
          <ProtectedRoute>
            <MainLayout>
              <AppraisalNrPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route path="/crew/nationality-manning" element={<Navigate to="/crew/statistics/nationalities" replace />} />
      <Route
        path="/crew/wage-allottee"
        element={
          <ProtectedRoute>
            <MainLayout>
              <WageAllotteePage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/crew/previous-service"
        element={
          <ProtectedRoute>
            <MainLayout>
              <PrevServiceExperiencePage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/crew/onboard-status"
        element={
          <ProtectedRoute>
            <MainLayout>
              <OnBoardStatusPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/crew/onboard-status/print"
        element={
          <ProtectedRoute>
            <MainLayout>
              <OnboardStatusReportPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/crew/replacement-planner"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ReplacementPlannerPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/crew/replacement-planner/print"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ReplacementPlanReportPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/crew/crew-changes-cost"
        element={
          <ProtectedRoute>
            <MainLayout>
              <TravelTicketsPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/crew/crew-changes-cost/print"
        element={
          <ProtectedRoute>
            <MainLayout>
              <CrewChangesCostReportPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/crew/crew-changes-cost/new"
        element={
          <ProtectedRoute>
            <MainLayout>
              <TravelTicketFormPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/crew/crew-changes-cost/:id/edit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <TravelTicketFormPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/crew/crew-changes-cost/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <TravelTicketDetailPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route path="/crew/travels" element={<CrewTravelsToChangesCostRedirect list />} />
      <Route path="/crew/travels/new" element={<CrewTravelsToChangesCostRedirect toNew />} />
      <Route path="/crew/travels/:id/edit" element={<CrewTravelsToChangesCostRedirect suffix="/edit" />} />
      <Route path="/crew/travels/:id" element={<CrewTravelsToChangesCostRedirect />} />
      <Route
        path="/crew/new"
        element={
          <ProtectedRoute>
            <MainLayout>
              <CrewForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/crew/:id/edit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <CrewForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/crew/list/print/:key"
        element={
          <ProtectedRoute>
            <MainLayout>
              <CrewListPrintPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route path="/crew/list/imo-print" element={<CrewImoPrintRedirect />} />
      <Route
        path="/crew/:id/file/print"
        element={
          <ProtectedRoute>
            <MainLayout>
              <CrewFileReportPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/crew/:id/file"
        element={
          <ProtectedRoute>
            <MainLayout>
              <CrewFileView />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/crew/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <CrewDetail />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      {/* Accounting */}
      <Route path="/accounting/journal-types" element={<Navigate to="/parameters/finance?section=journal-types" replace />} />
      <Route path="/accounting/journal-types/new" element={<ProtectedRoute><MainLayout><AccDocumentTypesForm /></MainLayout></ProtectedRoute>} />
      <Route path="/accounting/journal-types/:id/edit" element={<ProtectedRoute><MainLayout><AccDocumentTypesForm /></MainLayout></ProtectedRoute>} />
      <Route path="/accounting/journal-types/:id" element={<ProtectedRoute><MainLayout><AccDocumentTypesDetail /></MainLayout></ProtectedRoute>} />
      <Route path="/accounting/document-types/*" element={<AccountingDocumentTypesLegacyRedirect />} />
      <Route path="/accounting/account-types" element={<Navigate to="/parameters/finance?section=account-types" replace />} />
      <Route path="/accounting/account-types/new" element={<ProtectedRoute><MainLayout><AccAccountTypesForm /></MainLayout></ProtectedRoute>} />
      <Route path="/accounting/account-types/:id/edit" element={<ProtectedRoute><MainLayout><AccAccountTypesForm /></MainLayout></ProtectedRoute>} />
      <Route path="/accounting/account-types/:id" element={<ProtectedRoute><MainLayout><AccAccountTypesDetail /></MainLayout></ProtectedRoute>} />
      <Route path="/accounting/operational-types" element={<Navigate to="/parameters/finance?section=operational-types" replace />} />
      <Route path="/accounting/operational-types/new" element={<ProtectedRoute><MainLayout><AccOperationalTypesForm /></MainLayout></ProtectedRoute>} />
      <Route path="/accounting/operational-types/:id/edit" element={<ProtectedRoute><MainLayout><AccOperationalTypesForm /></MainLayout></ProtectedRoute>} />
      <Route path="/accounting/operational-types/:id" element={<ProtectedRoute><MainLayout><AccOperationalTypesDetail /></MainLayout></ProtectedRoute>} />
      <Route path="/accounting/companies" element={<Navigate to="/parameters/finance?section=companies" replace />} />
      <Route path="/accounting/companies/new" element={<ProtectedRoute><MainLayout><AccCompaniesForm /></MainLayout></ProtectedRoute>} />
      <Route path="/accounting/companies/:id/edit" element={<ProtectedRoute><MainLayout><AccCompaniesForm /></MainLayout></ProtectedRoute>} />
      <Route path="/accounting/companies/:id" element={<ProtectedRoute><MainLayout><AccCompaniesDetail /></MainLayout></ProtectedRoute>} />
      <Route path="/accounting/coa" element={<ProtectedRoute><MainLayout><AccAccountsList /></MainLayout></ProtectedRoute>} />
      <Route path="/accounting/coa/new" element={<ProtectedRoute><MainLayout><AccAccountsForm /></MainLayout></ProtectedRoute>} />
      <Route path="/accounting/coa/:id/edit" element={<ProtectedRoute><MainLayout><AccAccountsForm /></MainLayout></ProtectedRoute>} />
      <Route path="/accounting/coa/:id" element={<ProtectedRoute><MainLayout><AccAccountsDetail /></MainLayout></ProtectedRoute>} />
      <Route path="/accounting/periods" element={<Navigate to="/parameters/finance?section=periods" replace />} />
      <Route path="/accounting/periods/new" element={<ProtectedRoute><MainLayout><AccPeriodsForm /></MainLayout></ProtectedRoute>} />
      <Route path="/accounting/periods/:id/edit" element={<ProtectedRoute><MainLayout><AccPeriodsForm /></MainLayout></ProtectedRoute>} />
      <Route path="/accounting/periods/:id" element={<ProtectedRoute><MainLayout><AccPeriodsDetail /></MainLayout></ProtectedRoute>} />
      <Route path="/accounting/currency-rates" element={<ProtectedRoute><MainLayout><AccCurrencyRatesList /></MainLayout></ProtectedRoute>} />
      <Route path="/accounting/currency-rates/new" element={<ProtectedRoute><MainLayout><AccCurrencyRatesForm /></MainLayout></ProtectedRoute>} />
      <Route path="/accounting/currency-rates/:id/edit" element={<ProtectedRoute><MainLayout><AccCurrencyRatesForm /></MainLayout></ProtectedRoute>} />
      <Route path="/accounting/currency-rates/:id" element={<ProtectedRoute><MainLayout><AccCurrencyRatesDetail /></MainLayout></ProtectedRoute>} />
      <Route path="/accounting/statement-lines" element={<Navigate to="/parameters/finance?section=statement-lines" replace />} />
      <Route path="/accounting/payments/new" element={<ProtectedRoute><MainLayout><PaymentForm /></MainLayout></ProtectedRoute>} />
      <Route path="/accounting/payments/:id/edit" element={<ProtectedRoute><MainLayout><PaymentForm /></MainLayout></ProtectedRoute>} />
      <Route path="/accounting/invoices/new" element={<ProtectedRoute><MainLayout><InvoiceForm /></MainLayout></ProtectedRoute>} />
      <Route path="/accounting/invoices/:id/edit" element={<ProtectedRoute><MainLayout><InvoiceForm /></MainLayout></ProtectedRoute>} />
      <Route path="/accounting/journals" element={<ProtectedRoute><MainLayout><AccDocumentsList /></MainLayout></ProtectedRoute>} />
      <Route path="/accounting/journals/new" element={<ProtectedRoute><MainLayout><AccDocumentsForm /></MainLayout></ProtectedRoute>} />
      <Route path="/accounting/journals/:id/edit" element={<ProtectedRoute><MainLayout><AccDocumentsForm /></MainLayout></ProtectedRoute>} />
      <Route path="/accounting/journals/:id" element={<ProtectedRoute><MainLayout><AccDocumentsDetail /></MainLayout></ProtectedRoute>} />
      <Route path="/accounting/reports" element={<ProtectedRoute><MainLayout><ReportHub /></MainLayout></ProtectedRoute>} />
      <Route path="/accounting/reports/journal-register" element={<ProtectedRoute><MainLayout><JournalRegister /></MainLayout></ProtectedRoute>} />
      <Route path="/accounting/reports/journal" element={<ProtectedRoute><MainLayout><JournalReport /></MainLayout></ProtectedRoute>} />
      <Route path="/accounting/reports/analytical-ledgers" element={<ProtectedRoute><MainLayout><AnalyticalLedgers /></MainLayout></ProtectedRoute>} />
      <Route path="/accounting/reports/ledger-kartela/:accountId" element={<ProtectedRoute><MainLayout><LedgerKartela /></MainLayout></ProtectedRoute>} />
      <Route path="/accounting/reports/link-statement" element={<ProtectedRoute><MainLayout><LinkStatement /></MainLayout></ProtectedRoute>} />
      <Route path="/accounting/reports/trial-balance" element={<ProtectedRoute><MainLayout><TrialBalance /></MainLayout></ProtectedRoute>} />
      <Route path="/accounting/reports/trial-balance-by-company" element={<ProtectedRoute><MainLayout><TrialBalanceByCompany /></MainLayout></ProtectedRoute>} />
      <Route path="/accounting/reports/general-ledger" element={<ProtectedRoute><MainLayout><GeneralLedger /></MainLayout></ProtectedRoute>} />
      <Route path="/accounting/reports/open-items" element={<ProtectedRoute><MainLayout><OpenItemsDetail /></MainLayout></ProtectedRoute>} />
      <Route path="/accounting/reports/aging" element={<ProtectedRoute><MainLayout><Aging /></MainLayout></ProtectedRoute>} />
      <Route path="/accounting/reports/profit-and-loss" element={<ProtectedRoute><MainLayout><ProfitAndLoss /></MainLayout></ProtectedRoute>} />
      <Route path="/accounting/reports/balance-sheet" element={<ProtectedRoute><MainLayout><BalanceSheet /></MainLayout></ProtectedRoute>} />
      <Route path="/accounting/reports/intercompany" element={<ProtectedRoute><MainLayout><IntercompanyReport /></MainLayout></ProtectedRoute>} />
      <Route path="/accounting/reports/running-cost-by-vessel" element={<ProtectedRoute><MainLayout><RunningCostByVessel /></MainLayout></ProtectedRoute>} />
      <Route path="/accounting/reports/department-analysis" element={<ProtectedRoute><MainLayout><DepartmentAnalysis /></MainLayout></ProtectedRoute>} />
      <Route path="/accounting/reports/period-close-checklist" element={<ProtectedRoute><MainLayout><PeriodCloseChecklist /></MainLayout></ProtectedRoute>} />
      <Route path="/accounting/reports/audit-trail" element={<ProtectedRoute><MainLayout><AuditTrail /></MainLayout></ProtectedRoute>} />
      <Route
        path="/supplies/requisitions"
        element={
          <ProtectedRoute>
            <MainLayout>
              <RequisitionsList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/supplies/requisitions/new"
        element={
          <ProtectedRoute>
            <MainLayout>
              <RequisitionsForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/supplies/requisitions/:id/edit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <RequisitionsForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/supplies/requisitions/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <RequisitionsDetail />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/supplies/supplier-enquiries"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SupplierEnquiriesList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/supplies/supplier-enquiries/new"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SupplierEnquiriesForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/supplies/supplier-enquiries/:id/edit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SupplierEnquiriesForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/supplies/supplier-enquiries/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SupplierEnquiriesDetail />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/supplies/supplier-quotations"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SupplierQuotationsList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/supplies/supplier-quotations/new"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SupplierQuotationsForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/supplies/supplier-quotations/:id/edit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SupplierQuotationsForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/supplies/supplier-quotations/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SupplierQuotationsDetail />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/supplies/supply-deliveries"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SupplyDeliveriesList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/supplies/supply-deliveries/new"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SupplyDeliveriesForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/supplies/supply-deliveries/:id/edit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SupplyDeliveriesForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/supplies/supply-deliveries/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SupplyDeliveriesDetail />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/supplies/supply-purchase-orders"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SupplyPurchaseOrdersList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/supplies/supply-purchase-orders/new"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SupplyPurchaseOrdersForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/supplies/supply-purchase-orders/:id/edit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SupplyPurchaseOrdersForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/supplies/supply-purchase-orders/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SupplyPurchaseOrdersDetail />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route path="/supplies/supply-forwarding-statuses" element={<Navigate to="/parameters/purchasing?section=supplies-forwarding-statuses" replace />} />
      <Route
        path="/supplies/supply-forwarding-statuses/new"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SupplyForwardingStatusesForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/supplies/supply-forwarding-statuses/:id/edit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SupplyForwardingStatusesForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/supplies/supply-forwarding-statuses/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SupplyForwardingStatusesDetail />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/supplies/supply-forwardings"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SupplyForwardingsList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/supplies/supply-forwardings/new"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SupplyForwardingsForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/supplies/supply-forwardings/:id/edit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SupplyForwardingsForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/supplies/supply-forwardings/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SupplyForwardingsDetail />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/supplies/supply-invoices"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SupplyInvoicesList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/supplies/supply-invoices/new"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SupplyInvoicesForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/supplies/supply-invoices/:id/edit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SupplyInvoicesForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/supplies/supply-invoices/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SupplyInvoicesDetail />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/supplies/quotation-evaluations"
        element={
          <ProtectedRoute>
            <MainLayout>
              <QuotationEvaluationsList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/supplies/quotation-evaluations/new"
        element={
          <ProtectedRoute>
            <MainLayout>
              <QuotationEvaluationsForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/supplies/quotation-evaluations/:id/edit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <QuotationEvaluationsForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/supplies/quotation-evaluations/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <QuotationEvaluationsDetail />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/port-calls"
        element={
          <ProtectedRoute>
            <MainLayout>
              <PortCallsPage scope="all" />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/forwardings"
        element={
          <ProtectedRoute>
            <MainLayout>
              <CombinedForwardingsList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/supplies/port-calls"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SuppliesPortCallsPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/spares/port-calls"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SparesPortCallsPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/supplies/supply-cases"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SupplyCasesList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/supplies/supply-cases/new"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SupplyCasesForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/supplies/supply-cases/:id/case-view"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SupplyCasesCaseView />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/supplies/supply-cases/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SupplyCasesDetail />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/supplies/supply-cases/:id/edit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SupplyCasesForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route path="/supplies/items" element={<Navigate to="/parameters/purchasing?section=supplies-items" replace />} />
      <Route
        path="/supplies/items/new"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ItemsForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/supplies/items/:id/history"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ItemsItemHistory />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/supplies/items/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ItemsDetail />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/supplies/items/:id/edit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ItemsForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/supplies/store-catalogs"
        element={
          <ProtectedRoute>
            <MainLayout>
              <StoreCatalogsList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/supplies/store-catalogs/new"
        element={
          <ProtectedRoute>
            <MainLayout>
              <StoreCatalogsForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/supplies/store-catalogs/:id/edit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <StoreCatalogsForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/supplies/store-catalogs/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <StoreCatalogsDetail />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route path="/supplies/item-units" element={<Navigate to="/parameters/purchasing?section=supplies-units" replace />} />
      <Route
        path="/supplies/item-units/new"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ItemUnitsForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/supplies/item-units/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ItemUnitsDetail />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/supplies/item-units/:id/edit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ItemUnitsForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/supplies/excel-document-templates"
        element={
          <ProtectedRoute>
            <AdminRoute>
              <MainLayout>
                <ExcelDocumentTemplatesList />
              </MainLayout>
            </AdminRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/system/reference-templates"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ReferenceTemplatesList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/supplies/reference-templates"
        element={<Navigate to="/system/reference-templates?moduleId=supplies" replace />}
      />
      <Route
        path="/spares/reference-templates"
        element={<Navigate to="/system/reference-templates?moduleId=spares" replace />}
      />
      <Route
        path="/spares/requisitions"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SpareRequisitionsList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/spares/requisitions/new"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SpareRequisitionsForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/spares/requisitions/:id/edit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SpareRequisitionsForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/spares/requisitions/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SpareRequisitionsDetail />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/spares/supplier-enquiries"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SpareSupplierEnquiriesList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/spares/supplier-enquiries/new"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SpareSupplierEnquiriesForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/spares/supplier-enquiries/:id/edit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SpareSupplierEnquiriesForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/spares/supplier-enquiries/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SpareSupplierEnquiriesDetail />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/spares/supplier-quotations"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SpareSupplierQuotationsList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/spares/supplier-quotations/new"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SpareSupplierQuotationsForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/spares/supplier-quotations/:id/edit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SpareSupplierQuotationsForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/spares/supplier-quotations/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SpareSupplierQuotationsDetail />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/spares/spare-deliveries"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SpareDeliveriesList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/spares/spare-deliveries/new"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SpareDeliveriesForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/spares/spare-deliveries/:id/edit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SpareDeliveriesForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/spares/spare-deliveries/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SpareDeliveriesDetail />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/spares/spare-purchase-orders"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SparePurchaseOrdersList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/spares/spare-purchase-orders/new"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SparePurchaseOrdersForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/spares/spare-purchase-orders/:id/edit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SparePurchaseOrdersForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/spares/spare-purchase-orders/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SparePurchaseOrdersDetail />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route path="/spares/spare-forwarding-statuses" element={<Navigate to="/parameters/purchasing?section=spares-forwarding-statuses" replace />} />
      <Route
        path="/spares/spare-forwarding-statuses/new"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SpareForwardingStatusesForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/spares/spare-forwarding-statuses/:id/edit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SpareForwardingStatusesForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/spares/spare-forwarding-statuses/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SpareForwardingStatusesDetail />
            </MainLayout>
          </ProtectedRoute>
        }
      />

        <Route path="/pms/pms-record-statuses" element={<Navigate to="/parameters/pms?section=record-statuses" replace />} />
        <Route
          path="/pms/pms-record-statuses/new"
          element={
            <MainLayout>
              <PmsRecordStatusesForm />
            </MainLayout>
          }
        />
        <Route
          path="/pms/pms-record-statuses/:id/edit"
          element={
            <MainLayout>
              <PmsRecordStatusesForm />
            </MainLayout>
          }
        />
        <Route
          path="/pms/pms-record-statuses/:id"
          element={
            <MainLayout>
              <PmsRecordStatusesDetail />
            </MainLayout>
          }
        />

        <Route path="/pms/pms-criticalities" element={<Navigate to="/parameters/pms?section=criticalities" replace />} />
        <Route
          path="/pms/pms-criticalities/new"
          element={
            <MainLayout>
              <PmsCriticalitiesForm />
            </MainLayout>
          }
        />
        <Route
          path="/pms/pms-criticalities/:id/edit"
          element={
            <MainLayout>
              <PmsCriticalitiesForm />
            </MainLayout>
          }
        />
        <Route
          path="/pms/pms-criticalities/:id"
          element={
            <MainLayout>
              <PmsCriticalitiesDetail />
            </MainLayout>
          }
        />

        <Route path="/pms/pms-equipment-statuses" element={<Navigate to="/parameters/pms?section=equipment-statuses" replace />} />
        <Route
          path="/pms/pms-equipment-statuses/new"
          element={
            <MainLayout>
              <PmsEquipmentStatusesForm />
            </MainLayout>
          }
        />
        <Route
          path="/pms/pms-equipment-statuses/:id/edit"
          element={
            <MainLayout>
              <PmsEquipmentStatusesForm />
            </MainLayout>
          }
        />
        <Route
          path="/pms/pms-equipment-statuses/:id"
          element={
            <MainLayout>
              <PmsEquipmentStatusesDetail />
            </MainLayout>
          }
        />

        <Route path="/pms/pms-maintenance-strategies" element={<Navigate to="/parameters/pms?section=maintenance-strategies" replace />} />
        <Route
          path="/pms/pms-maintenance-strategies/new"
          element={
            <MainLayout>
              <PmsMaintenanceStrategiesForm />
            </MainLayout>
          }
        />
        <Route
          path="/pms/pms-maintenance-strategies/:id/edit"
          element={
            <MainLayout>
              <PmsMaintenanceStrategiesForm />
            </MainLayout>
          }
        />
        <Route
          path="/pms/pms-maintenance-strategies/:id"
          element={
            <MainLayout>
              <PmsMaintenanceStrategiesDetail />
            </MainLayout>
          }
        />

        <Route path="/pms/pms-job-types" element={<Navigate to="/parameters/pms?section=job-types" replace />} />
        <Route
          path="/pms/pms-job-types/new"
          element={
            <MainLayout>
              <PmsJobTypesForm />
            </MainLayout>
          }
        />
        <Route
          path="/pms/pms-job-types/:id/edit"
          element={
            <MainLayout>
              <PmsJobTypesForm />
            </MainLayout>
          }
        />
        <Route
          path="/pms/pms-job-types/:id"
          element={
            <MainLayout>
              <PmsJobTypesDetail />
            </MainLayout>
          }
        />

        <Route path="/pms/pms-schedule-types" element={<Navigate to="/parameters/pms?section=schedule-types" replace />} />
        <Route
          path="/pms/pms-schedule-types/new"
          element={
            <MainLayout>
              <PmsScheduleTypesForm />
            </MainLayout>
          }
        />
        <Route
          path="/pms/pms-schedule-types/:id/edit"
          element={
            <MainLayout>
              <PmsScheduleTypesForm />
            </MainLayout>
          }
        />
        <Route
          path="/pms/pms-schedule-types/:id"
          element={
            <MainLayout>
              <PmsScheduleTypesDetail />
            </MainLayout>
          }
        />

        <Route path="/pms/pms-interval-units" element={<Navigate to="/parameters/pms?section=interval-units" replace />} />
        <Route
          path="/pms/pms-interval-units/new"
          element={
            <MainLayout>
              <PmsIntervalUnitsForm />
            </MainLayout>
          }
        />
        <Route
          path="/pms/pms-interval-units/:id/edit"
          element={
            <MainLayout>
              <PmsIntervalUnitsForm />
            </MainLayout>
          }
        />
        <Route
          path="/pms/pms-interval-units/:id"
          element={
            <MainLayout>
              <PmsIntervalUnitsDetail />
            </MainLayout>
          }
        />

        <Route path="/pms/pms-work-order-statuses" element={<Navigate to="/parameters/pms?section=work-order-statuses" replace />} />
        <Route
          path="/pms/pms-work-order-statuses/new"
          element={
            <MainLayout>
              <PmsWorkOrderStatusesForm />
            </MainLayout>
          }
        />
        <Route
          path="/pms/pms-work-order-statuses/:id/edit"
          element={
            <MainLayout>
              <PmsWorkOrderStatusesForm />
            </MainLayout>
          }
        />
        <Route
          path="/pms/pms-work-order-statuses/:id"
          element={
            <MainLayout>
              <PmsWorkOrderStatusesDetail />
            </MainLayout>
          }
        />

        <Route path="/pms/pms-work-order-sources" element={<Navigate to="/parameters/pms?section=work-order-sources" replace />} />
        <Route
          path="/pms/pms-work-order-sources/new"
          element={
            <MainLayout>
              <PmsWorkOrderSourcesForm />
            </MainLayout>
          }
        />
        <Route
          path="/pms/pms-work-order-sources/:id/edit"
          element={
            <MainLayout>
              <PmsWorkOrderSourcesForm />
            </MainLayout>
          }
        />
        <Route
          path="/pms/pms-work-order-sources/:id"
          element={
            <MainLayout>
              <PmsWorkOrderSourcesDetail />
            </MainLayout>
          }
        />

        <Route path="/pms/pms-approval-statuses" element={<Navigate to="/parameters/pms?section=approval-statuses" replace />} />
        <Route
          path="/pms/pms-approval-statuses/new"
          element={
            <MainLayout>
              <PmsApprovalStatusesForm />
            </MainLayout>
          }
        />
        <Route
          path="/pms/pms-approval-statuses/:id/edit"
          element={
            <MainLayout>
              <PmsApprovalStatusesForm />
            </MainLayout>
          }
        />
        <Route
          path="/pms/pms-approval-statuses/:id"
          element={
            <MainLayout>
              <PmsApprovalStatusesDetail />
            </MainLayout>
          }
        />

        <Route path="/pms/pms-step-statuses" element={<Navigate to="/parameters/pms?section=step-statuses" replace />} />
        <Route
          path="/pms/pms-step-statuses/new"
          element={
            <MainLayout>
              <PmsStepStatusesForm />
            </MainLayout>
          }
        />
        <Route
          path="/pms/pms-step-statuses/:id/edit"
          element={
            <MainLayout>
              <PmsStepStatusesForm />
            </MainLayout>
          }
        />
        <Route
          path="/pms/pms-step-statuses/:id"
          element={
            <MainLayout>
              <PmsStepStatusesDetail />
            </MainLayout>
          }
        />

        <Route path="/pms/pms-counter-types" element={<Navigate to="/parameters/pms?section=counter-types" replace />} />
        <Route
          path="/pms/pms-counter-types/new"
          element={
            <MainLayout>
              <PmsCounterTypesForm />
            </MainLayout>
          }
        />
        <Route
          path="/pms/pms-counter-types/:id/edit"
          element={
            <MainLayout>
              <PmsCounterTypesForm />
            </MainLayout>
          }
        />
        <Route
          path="/pms/pms-counter-types/:id"
          element={
            <MainLayout>
              <PmsCounterTypesDetail />
            </MainLayout>
          }
        />

        <Route path="/pms/pms-counter-sources" element={<Navigate to="/parameters/pms?section=counter-sources" replace />} />
        <Route
          path="/pms/pms-counter-sources/new"
          element={
            <MainLayout>
              <PmsCounterSourcesForm />
            </MainLayout>
          }
        />
        <Route
          path="/pms/pms-counter-sources/:id/edit"
          element={
            <MainLayout>
              <PmsCounterSourcesForm />
            </MainLayout>
          }
        />
        <Route
          path="/pms/pms-counter-sources/:id"
          element={
            <MainLayout>
              <PmsCounterSourcesDetail />
            </MainLayout>
          }
        />

        <Route path="/pms/pms-measurement-results" element={<Navigate to="/parameters/pms?section=measurement-results" replace />} />
        <Route
          path="/pms/pms-measurement-results/new"
          element={
            <MainLayout>
              <PmsMeasurementResultsForm />
            </MainLayout>
          }
        />
        <Route
          path="/pms/pms-measurement-results/:id/edit"
          element={
            <MainLayout>
              <PmsMeasurementResultsForm />
            </MainLayout>
          }
        />
        <Route
          path="/pms/pms-measurement-results/:id"
          element={
            <MainLayout>
              <PmsMeasurementResultsDetail />
            </MainLayout>
          }
        />

        <Route path="/pms/pms-defect-severities" element={<Navigate to="/parameters/pms?section=defect-severities" replace />} />
        <Route
          path="/pms/pms-defect-severities/new"
          element={
            <MainLayout>
              <PmsDefectSeveritiesForm />
            </MainLayout>
          }
        />
        <Route
          path="/pms/pms-defect-severities/:id/edit"
          element={
            <MainLayout>
              <PmsDefectSeveritiesForm />
            </MainLayout>
          }
        />
        <Route
          path="/pms/pms-defect-severities/:id"
          element={
            <MainLayout>
              <PmsDefectSeveritiesDetail />
            </MainLayout>
          }
        />

        <Route path="/pms/pms-defect-statuses" element={<Navigate to="/parameters/pms?section=defect-statuses" replace />} />
        <Route
          path="/pms/pms-defect-statuses/new"
          element={
            <MainLayout>
              <PmsDefectStatusesForm />
            </MainLayout>
          }
        />
        <Route
          path="/pms/pms-defect-statuses/:id/edit"
          element={
            <MainLayout>
              <PmsDefectStatusesForm />
            </MainLayout>
          }
        />
        <Route
          path="/pms/pms-defect-statuses/:id"
          element={
            <MainLayout>
              <PmsDefectStatusesDetail />
            </MainLayout>
          }
        />

        <Route path="/pms/pms-equipment-instance-statuses" element={<Navigate to="/parameters/pms?section=equipment-instance-statuses" replace />} />
        <Route
          path="/pms/pms-equipment-instance-statuses/new"
          element={
            <MainLayout>
              <PmsEquipmentInstanceStatusesForm />
            </MainLayout>
          }
        />
        <Route
          path="/pms/pms-equipment-instance-statuses/:id/edit"
          element={
            <MainLayout>
              <PmsEquipmentInstanceStatusesForm />
            </MainLayout>
          }
        />
        <Route
          path="/pms/pms-equipment-instance-statuses/:id"
          element={
            <MainLayout>
              <PmsEquipmentInstanceStatusesDetail />
            </MainLayout>
          }
        />

        <Route path="/pms/pms-safety-requirement-types" element={<Navigate to="/parameters/pms?section=safety-requirement-types" replace />} />
        <Route
          path="/pms/pms-safety-requirement-types/new"
          element={
            <MainLayout>
              <PmsSafetyRequirementTypesForm />
            </MainLayout>
          }
        />
        <Route
          path="/pms/pms-safety-requirement-types/:id/edit"
          element={
            <MainLayout>
              <PmsSafetyRequirementTypesForm />
            </MainLayout>
          }
        />
        <Route
          path="/pms/pms-safety-requirement-types/:id"
          element={
            <MainLayout>
              <PmsSafetyRequirementTypesDetail />
            </MainLayout>
          }
        />

        <Route path="/pms/pms-document-types" element={<Navigate to="/parameters/pms?section=document-types" replace />} />
        <Route
          path="/pms/pms-document-types/new"
          element={
            <MainLayout>
              <PmsDocumentTypesForm />
            </MainLayout>
          }
        />
        <Route
          path="/pms/pms-document-types/:id/edit"
          element={
            <MainLayout>
              <PmsDocumentTypesForm />
            </MainLayout>
          }
        />
        <Route
          path="/pms/pms-document-types/:id"
          element={
            <MainLayout>
              <PmsDocumentTypesDetail />
            </MainLayout>
          }
        />

        <Route path="/pms/pms-verification-statuses" element={<Navigate to="/parameters/pms?section=verification-statuses" replace />} />
        <Route
          path="/pms/pms-verification-statuses/new"
          element={
            <MainLayout>
              <PmsVerificationStatusesForm />
            </MainLayout>
          }
        />
        <Route
          path="/pms/pms-verification-statuses/:id/edit"
          element={
            <MainLayout>
              <PmsVerificationStatusesForm />
            </MainLayout>
          }
        />
        <Route
          path="/pms/pms-verification-statuses/:id"
          element={
            <MainLayout>
              <PmsVerificationStatusesDetail />
            </MainLayout>
          }
        />

        <Route path="/pms/pms-department-types" element={<Navigate to="/parameters/pms?section=department-types" replace />} />
        <Route
          path="/pms/pms-department-types/new"
          element={
            <MainLayout>
              <PmsDepartmentTypesForm />
            </MainLayout>
          }
        />
        <Route
          path="/pms/pms-department-types/:id/edit"
          element={
            <MainLayout>
              <PmsDepartmentTypesForm />
            </MainLayout>
          }
        />
        <Route
          path="/pms/pms-department-types/:id"
          element={
            <MainLayout>
              <PmsDepartmentTypesDetail />
            </MainLayout>
          }
        />

        <Route
          path="/pms/hierarchy"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PmsHierarchyPage />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/pms/due-jobs"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PmsDueJobsPage />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        <Route path="/pms/pms-departments" element={<MainLayout><PmsDepartmentsList /></MainLayout>} />
        <Route path="/pms/pms-departments/new" element={<MainLayout><PmsDepartmentsForm /></MainLayout>} />
        <Route path="/pms/pms-departments/:id/edit" element={<MainLayout><PmsDepartmentsForm /></MainLayout>} />
        <Route path="/pms/pms-departments/:id" element={<MainLayout><PmsDepartmentsDetail /></MainLayout>} />

        <Route path="/pms/pms-equipment-systems" element={<MainLayout><PmsEquipmentSystemsList /></MainLayout>} />
        <Route path="/pms/pms-equipment-systems/new" element={<MainLayout><PmsEquipmentSystemsForm /></MainLayout>} />
        <Route path="/pms/pms-equipment-systems/:id/edit" element={<MainLayout><PmsEquipmentSystemsForm /></MainLayout>} />
        <Route path="/pms/pms-equipment-systems/:id" element={<MainLayout><PmsEquipmentSystemsDetail /></MainLayout>} />

        <Route path="/pms/pms-components" element={<MainLayout><PmsComponentsList /></MainLayout>} />
        <Route path="/pms/pms-components/new" element={<MainLayout><PmsComponentsForm /></MainLayout>} />
        <Route path="/pms/pms-components/:id/edit" element={<MainLayout><PmsComponentsForm /></MainLayout>} />
        <Route path="/pms/pms-components/:id" element={<MainLayout><PmsComponentsDetail /></MainLayout>} />

        <Route path="/pms/pms-sub-components" element={<MainLayout><PmsSubComponentsList /></MainLayout>} />
        <Route path="/pms/pms-sub-components/new" element={<MainLayout><PmsSubComponentsForm /></MainLayout>} />
        <Route path="/pms/pms-sub-components/:id/edit" element={<MainLayout><PmsSubComponentsForm /></MainLayout>} />
        <Route path="/pms/pms-sub-components/:id" element={<MainLayout><PmsSubComponentsDetail /></MainLayout>} />

        <Route path="/pms/pms-equipment" element={<MainLayout><PmsEquipmentList /></MainLayout>} />
        <Route path="/pms/pms-equipment/new" element={<MainLayout><PmsEquipmentForm /></MainLayout>} />
        <Route path="/pms/pms-equipment/:id/edit" element={<MainLayout><PmsEquipmentForm /></MainLayout>} />
        <Route path="/pms/pms-equipment/:id" element={<MainLayout><PmsEquipmentDetail /></MainLayout>} />

        <Route path="/pms/pms-equipment-instances" element={<MainLayout><PmsEquipmentInstancesList /></MainLayout>} />
        <Route path="/pms/pms-equipment-instances/new" element={<MainLayout><PmsEquipmentInstancesForm /></MainLayout>} />
        <Route path="/pms/pms-equipment-instances/:id/edit" element={<MainLayout><PmsEquipmentInstancesForm /></MainLayout>} />
        <Route path="/pms/pms-equipment-instances/:id" element={<MainLayout><PmsEquipmentInstancesDetail /></MainLayout>} />

        <Route path="/pms/pms-maintenance-jobs" element={<MainLayout><PmsMaintenanceJobsList /></MainLayout>} />
        <Route path="/pms/pms-maintenance-jobs/new" element={<MainLayout><PmsMaintenanceJobsForm /></MainLayout>} />
        <Route path="/pms/pms-maintenance-jobs/:id/edit" element={<MainLayout><PmsMaintenanceJobsForm /></MainLayout>} />
        <Route path="/pms/pms-maintenance-jobs/:id" element={<MainLayout><PmsMaintenanceJobsDetail /></MainLayout>} />

        <Route path="/pms/pms-maintenance-counters" element={<MainLayout><PmsMaintenanceCountersList /></MainLayout>} />
        <Route path="/pms/pms-maintenance-counters/new" element={<MainLayout><PmsMaintenanceCountersForm /></MainLayout>} />
        <Route path="/pms/pms-maintenance-counters/:id/edit" element={<MainLayout><PmsMaintenanceCountersForm /></MainLayout>} />
        <Route path="/pms/pms-maintenance-counters/:id" element={<MainLayout><PmsMaintenanceCountersDetail /></MainLayout>} />

        <Route path="/pms/pms-work-orders" element={<MainLayout><PmsWorkOrdersList /></MainLayout>} />
        <Route path="/pms/pms-work-orders/new" element={<MainLayout><PmsWorkOrdersForm /></MainLayout>} />
        <Route path="/pms/pms-work-orders/:id/edit" element={<MainLayout><PmsWorkOrdersForm /></MainLayout>} />
        <Route path="/pms/pms-work-orders/:id" element={<MainLayout><PmsWorkOrdersDetail /></MainLayout>} />

        <Route path="/pms/pms-defects" element={<MainLayout><PmsDefectsList /></MainLayout>} />
        <Route path="/pms/pms-defects/new" element={<MainLayout><PmsDefectsForm /></MainLayout>} />
        <Route path="/pms/pms-defects/:id/edit" element={<MainLayout><PmsDefectsForm /></MainLayout>} />
        <Route path="/pms/pms-defects/:id" element={<MainLayout><PmsDefectsDetail /></MainLayout>} />
        <Route path="/operations/noon-reports" element={<MainLayout><NoonReportsList /></MainLayout>} />
        <Route path="/operations/noon-reports/new" element={<MainLayout><NoonReportsForm /></MainLayout>} />
        <Route path="/operations/noon-reports/:id/edit" element={<MainLayout><NoonReportsForm /></MainLayout>} />
        <Route path="/operations/noon-reports/:id" element={<MainLayout><NoonReportsDetail /></MainLayout>} />

        <Route path="/pms/pms-tools" element={<MainLayout><PmsToolsList /></MainLayout>} />
        <Route path="/pms/pms-tools/new" element={<MainLayout><PmsToolsForm /></MainLayout>} />
        <Route path="/pms/pms-tools/:id/edit" element={<MainLayout><PmsToolsForm /></MainLayout>} />
        <Route path="/pms/pms-tools/:id" element={<MainLayout><PmsToolsDetail /></MainLayout>} />

        <Route path="/pms/pms-documents" element={<ProtectedRoute><MainLayout><PmsDocumentsList /></MainLayout></ProtectedRoute>} />
        <Route path="/pms/pms-documents/new" element={<ProtectedRoute><MainLayout><PmsDocumentsForm /></MainLayout></ProtectedRoute>} />
        <Route path="/pms/pms-documents/:id/edit" element={<ProtectedRoute><MainLayout><PmsDocumentsForm /></MainLayout></ProtectedRoute>} />
        <Route path="/pms/pms-documents/:id" element={<ProtectedRoute><MainLayout><PmsDocumentsDetail /></MainLayout></ProtectedRoute>} />
      <Route
        path="/spares/spare-forwardings"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SpareForwardingsList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/spares/spare-forwardings/new"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SpareForwardingsForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/spares/spare-forwardings/:id/edit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SpareForwardingsForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/spares/spare-forwardings/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SpareForwardingsDetail />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/spares/spare-invoices"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SpareInvoicesList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/spares/spare-invoices/new"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SpareInvoicesForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/spares/spare-invoices/:id/edit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SpareInvoicesForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/spares/spare-invoices/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SpareInvoicesDetail />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/spares/quotation-evaluations"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SpareQuotationEvaluationsList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/spares/quotation-evaluations/new"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SpareQuotationEvaluationsForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/spares/quotation-evaluations/:id/edit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SpareQuotationEvaluationsForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/spares/quotation-evaluations/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SpareQuotationEvaluationsDetail />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/spares/spare-cases"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SpareCasesList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/spares/spare-cases/new"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SpareCasesForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/spares/spare-cases/:id/case-view"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SpareCasesCaseView />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/spares/spare-cases/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SpareCasesDetail />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/spares/spare-cases/:id/edit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SpareCasesForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/spares/spare-libraries"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SpareLibrariesList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/spares/spare-libraries/new"
        element={<Navigate to="/spares/spare-libraries?new=1" replace />}
      />
      <Route
        path="/spares/spare-libraries/:id/edit"
        element={<SpareLibraryEditRedirect />}
      />
      <Route
        path="/spares/spare-libraries/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SpareLibrariesDetail />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route path="/spares/spare-items" element={<Navigate to="/parameters/purchasing?section=spares-items" replace />} />
      <Route
        path="/spares/spare-items/new"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SpareItemsForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/spares/spare-items/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SpareItemsDetail />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/spares/spare-items/:id/edit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SpareItemsForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/account-settings"
        element={
          <ProtectedRoute>
            <MainLayout>
              <AccountSettings />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/settings/application"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ApplicationSettingsPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/module-management"
        element={
          <ProtectedRoute>
            <AdminRoute>
              <MainLayout>
                <ModuleManagementPage />
              </MainLayout>
            </AdminRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/ai-billing"
        element={
          <ProtectedRoute>
            <MainLayout>
              <AiBillingPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/database-browser"
        element={
          <ProtectedRoute>
            <AdminRoute>
              <MainLayout>
                <DatabaseBrowserPage />
              </MainLayout>
            </AdminRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/import-actions/impa-code"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ImpaCodeImportPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/import-actions/accounts-plan"
        element={
          <ProtectedRoute>
            <MainLayout>
              <AccountsPlanImportPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/import-actions/companies"
        element={
          <ProtectedRoute>
            <MainLayout>
              <CompaniesImportPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/import-actions/journals"
        element={
          <ProtectedRoute>
            <MainLayout>
              <JournalsImportPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

const AccountingDocumentTypesLegacyRedirect: React.FC = () => {
  const { pathname } = useLocation();
  return <Navigate to={pathname.replace('/accounting/document-types', '/accounting/journal-types')} replace />;
};

const DefaultLandingRoute: React.FC = () => {
  const { user, permissions } = useAuth();
  if (!user) return <Navigate to="/dashboard" replace />;
  if (user.role === 'ADMIN') return <Navigate to="/dashboard" replace />;
  const canView = (moduleKey: string) => Boolean(permissions[moduleKey]?.canView);
  for (const moduleKey of LANDING_MODULE_ORDER) {
    if (!canView(moduleKey)) continue;
    const path = resolveLandingPathForModuleKey(moduleKey, user.vesselId);
    if (path) return <Navigate to={path} replace />;
  }
  return <Navigate to="/dashboard" replace />;
};

const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <NotificationProvider>
          <ConfirmProvider>
          <LoadingProvider>
            <Router
              future={{
                v7_startTransition: true,
                v7_relativeSplatPath: true,
              }}
            >
              <SWRConfigProvider>
                <AuthProvider>
                  <ChatProvider>
                    <SettingsProvider>
                      <TopbarProvider>
                        <GlobalLoading />
                        <OfficeVesselRedirect>
                          <ManningAgentRouteGuard>
                            <AppRoutes />
                          </ManningAgentRouteGuard>
                        </OfficeVesselRedirect>
                      </TopbarProvider>
                    </SettingsProvider>
                  </ChatProvider>
                </AuthProvider>
              </SWRConfigProvider>
            </Router>
          </LoadingProvider>
          </ConfirmProvider>
        </NotificationProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
};

export default App;
